// ─── Trailing Stop Engine v4 ──────────────────────────────────
// Thuật toán chính xác theo yêu cầu:
//
// STEP 1 (luôn chạy): Rà soát tất cả lệnh đang mở.
//   Nếu lệnh CHƯA có SL và TP → lập tức set SL & TP theo params.
//   CHỈ áp dụng cho FOREX. Vàng/bạc/dầu/chứng khoán → bỏ qua.
//
// STEP 2 (tuỳ chọn - mode='full'): Theo dõi profit.
//   Khi profit đạt profitReach → SL = entry point (breakeven, x=0).
//   Mỗi step pips tiếp theo → SL tịnh tiến thêm step:
//     BUY:  SL = entry + x * step * pipSize  (x: 0 → 200)
//     SELL: SL = entry - x * step * pipSize  (x: 0 → 200)
//   Trong đó x = floor((highestProfit - profitReach) / step), clamp [0, 200].
//
// Giá: reconcile trả về raw integer (108500 = 1.08500).
//      Spot ticks trả về decimal (1.08500).
//      Engine chuẩn hoá tất cả về decimal để tính toán,
//      và convert về raw khi gọi amend-sltp.

import { supabase } from './supabase';
import { buildSessionKey } from './session-key';
import { getAccountData } from './ctrader-gateway-client';
import { ctraderPriceBus, type SpotTick } from './ctrader-price-bus';
import { copyTradePositionsBus } from './copy-trade-positions-bus';
import {
  ensureStream,
  releaseStream,
  updateStreamSymbols,
} from './background-stream-manager';
import {
  parseReconcileRes,
  parseSymbolRes,
  parseTraderRes,
  type ParsedPosition,
} from './ctrader-parsers';

// ─── Types ─────────────────────────────────────────────────────

export interface TrailingStopParams {
  mode: 'setupOnly' | 'simple' | 'full';
  profitReach: number; // pips to hit breakeven
  step: number;        // pips per trailing increment
  takeProfit: number;  // pips TP
  stopLoss: number;    // pips initial SL
  usePriceSLTP: boolean;           // tick: set TP/SL by actual price instead of pips
  takeProfitPrice: number | null;  // absolute TP price when usePriceSLTP
  stopLossPrice: number | null;    // absolute SL price when usePriceSLTP
}

export const DEFAULT_TRAILING_STOP_PARAMS: TrailingStopParams = {
  mode: 'full',
  profitReach: 20,
  step: 5,
  takeProfit: 50,
  stopLoss: 100,
  usePriceSLTP: false,
  takeProfitPrice: null,
  stopLossPrice: null,
};

/**
 * Fresh-trade defaults applied when a trailing session ends (all positions
 * closed / no open orders). These are intentionally DIFFERENT from
 * DEFAULT_TRAILING_STOP_PARAMS (the initial fallback for strategies with no
 * configured params): the user's requested reset values use stopLoss = 50.
 * `mode` is not included here — the reset preserves the current mode.
 */
export const RESET_TRAILING_STOP_PARAMS: Omit<TrailingStopParams, 'mode'> = {
  profitReach: 20,
  step: 5,
  takeProfit: 50,
  stopLoss: 50,
  usePriceSLTP: false,
  takeProfitPrice: null,
  stopLossPrice: null,
};

/**
 * Normalise any incoming trailing params into a fully-valid TrailingStopParams.
 *
 * Historically `strategies.trailingstopparams` was written WITHOUT a `mode`
 * field (only profitReach/step/takeProfit/stopLoss). When that object was used
 * as-is, `mode` became `undefined`, which then failed validation on load and
 * silently fell back to DEFAULT — making every edit appear to be ignored.
 *
 * This helper merges with DEFAULT and coerces each numeric field so that any
 * partial/legacy payload always yields a complete, valid params object.
 */
export function normalizeTrailingParams(
  p: Partial<TrailingStopParams> | null | undefined,
): TrailingStopParams {
  const src = p && typeof p === 'object' ? (p as Record<string, unknown>) : {};
  const validModes: TrailingStopParams['mode'][] = ['setupOnly', 'simple', 'full'];
  const mode = validModes.includes(src.mode as TrailingStopParams['mode'])
    ? (src.mode as TrailingStopParams['mode'])
    : 'full';
  const num = (v: unknown, d: number): number =>
    typeof v === 'number' && Number.isFinite(v) ? v : d;
  const price = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null;

  return {
    mode,
    profitReach: num(src.profitReach, DEFAULT_TRAILING_STOP_PARAMS.profitReach),
    step: num(src.step, DEFAULT_TRAILING_STOP_PARAMS.step),
    takeProfit: num(src.takeProfit, DEFAULT_TRAILING_STOP_PARAMS.takeProfit),
    stopLoss: num(src.stopLoss, DEFAULT_TRAILING_STOP_PARAMS.stopLoss),
    usePriceSLTP: src.usePriceSLTP === true,
    takeProfitPrice: price(src.takeProfitPrice),
    stopLossPrice: price(src.stopLossPrice),
  };
}

export interface TrackedPosition {
  positionId: string;
  symbolName: string;
  symbolId: number;
  digits: number;
  direction: 'buy' | 'sell';
  entryPrice: number;        // decimal (e.g. 1.08500)
  currentPrice: number;      // decimal
  currentSL: number | null;  // decimal — actual SL on cTrader
  currentTP: number | null;  // decimal — actual TP on cTrader
  targetSL: number | null;   // decimal — what engine wants SL to be
  targetTP: number | null;   // decimal — what engine wants TP to be
  sltpSetupDone: boolean;    // true after initial SL+TP have been set via amend-sltp
  slTrailed: boolean;        // true once the trailing engine has moved SL (breakeven/stepped)
  isForex: boolean;
  rawVolume: number;         // raw cTrader volume units
  volume: number;            // lots (display)
  profitPips: number;
  entryTime: number;
  lastSLModifyTime: number;
  priceScale: number;        // 10^digits — for raw↔decimal conversion
}

export interface TrailingStopSnapshot {
  positions: Record<string, TrackedPosition>;
  lastTick: number;
  isRunning: boolean;
  selectedAccountId: string;
  alerts: TrailingStopAlert[];
  streamActive: boolean;
  loading: boolean;
  lastError: string;
  currentMode: 'setupOnly' | 'simple' | 'full';
  depositCurrency: string;
  paramsResetSignal: number;
}

export interface TrailingStopAlert {
  id: string;
  type: 'initial_sltp_set' | 'breakeven' | 'sl_trailed' | 'tp_hit' | 'position_opened' | 'position_closed' | 'error' | 'info';
  message: string;
  timestamp: number;
  symbolName?: string;
}

// ─── Constants ──────────────────────────────────────────────────

const MAX_TRAILING_STEPS = 200;
const MIN_AMEND_INTERVAL_MS = 2000; // min time between amend-sltp calls per position
const POSITION_REFRESH_INTERVAL_MS = 300000; // 5-min safety re-sync of positions (live price trailing is tick-by-tick via spot stream)

// Symbols that are NOT forex
const NON_FOREX_KEYWORDS = [
  'XAU', 'XAG', 'XPD', 'XPT', // metals
  'OIL', 'GAS', 'BRENT', 'WTI', // energies
  'BTC', 'ETH', 'LTC', 'XRP', 'BCH', 'USDT', // crypto
  'US30', 'US100', 'US500', 'GER40', 'GER30', 'UK100', 'JPN225', // indices
  'SPX', 'NDX', 'DJI', 'VIX', 'DAX', 'FTSE', 'NIKKEI', // more indices
  'AUS200', 'ESP35', 'FRA40', 'HKG33', 'STOXX', 'NAS', 'DOW', 'S&P',
];

// ─── Helpers ───────────────────────────────────────────────────

function isForexSymbol(symbolName: string): boolean {
  const upper = symbolName.toUpperCase();
  return !NON_FOREX_KEYWORDS.some((kw) => upper.includes(kw));
}

function priceScale(digits: number): number {
  return Math.pow(10, digits);
}

function pipSizeDecimal(digits: number): number {
  return Math.pow(10, -digits + 1);
}

/** Convert raw cTrader integer price to decimal */
function toDecimal(raw: number, digits: number): number {
  return raw / priceScale(digits);
}

/** Convert decimal price to raw cTrader integer */
function toRaw(decimal: number, digits: number): number {
  return Math.round(decimal * priceScale(digits));
}

function calcProfitPips(
  entryPrice: number,
  currentPrice: number,
  direction: 'buy' | 'sell',
  digits: number,
): number {
  const pip = pipSizeDecimal(digits);
  if (direction === 'buy') return (currentPrice - entryPrice) / pip;
  return (entryPrice - currentPrice) / pip;
}

/** Round price to exact number of decimal digits to avoid floating-point noise */
function roundPrice(price: number, digits: number): number {
  const scale = Math.pow(10, digits);
  return Math.round(price * scale) / scale;
}

function guessDigits(symbolName: string): number {
  const upper = symbolName.toUpperCase();
  if (upper.includes('JPY')) return 3;
  if (upper.includes('XAU') || upper.includes('XAG')) return 2;
  return 5;
}

function genAlertId(): string {
  return `ts-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Engine Class ──────────────────────────────────────────────

export class TrailingStopEngine {
  private params: TrailingStopParams;
  private accountId: string = '';
  private positions: Record<string, TrackedPosition> = {};
  private alerts: TrailingStopAlert[] = [];
  private lastTick: number = 0;
  private running: boolean = false;
  private refreshInterval: ReturnType<typeof setInterval> | null = null;
  private fallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners: Set<(s: TrailingStopSnapshot) => void> = new Set();
  private loading: boolean = false;
  private unsubBus: (() => void) | null = null;
  private unsubPositions: (() => void) | null = null;
  private lastPositionsRefresh: number = 0;
  private streamActive: boolean = false;
  private lastError: string = '';
  private amendPending: Set<string> = new Set(); // positionIds currently being modified
  private tpClosePending: Set<string> = new Set(); // positionIds currently being closed due to TP hit
  private depositCurrency: string = '';
  private paramsResetSignal: number = 0;

  constructor(params: TrailingStopParams) {
    this.params = normalizeTrailingParams(params);
  }

  get isRunning(): boolean { return this.running; }
  get selectedAccountId(): string { return this.accountId; }
  get currentParams(): TrailingStopParams { return this.params; }

  setParams(p: TrailingStopParams): void {
    const np = normalizeTrailingParams(p);
    const changed =
      np.mode !== this.params.mode ||
      np.profitReach !== this.params.profitReach ||
      np.step !== this.params.step ||
      np.takeProfit !== this.params.takeProfit ||
      np.stopLoss !== this.params.stopLoss ||
      np.usePriceSLTP !== this.params.usePriceSLTP ||
      np.takeProfitPrice !== this.params.takeProfitPrice ||
      np.stopLossPrice !== this.params.stopLossPrice;

    this.params = np;

    // Khi setting thay đổi, áp dụng lại SL/TP mới cho các order ĐANG chạy
    // (chỉ những order đã setup SL/TP, vì order chưa setup sẽ được xử lý ở STEP 1).
    if (changed && this.running && Object.keys(this.positions).length > 0) {
      void this.reapplyParamsToPositions();
    }
  }

  // ─── Reset params to defaults when there are no open positions ─────
  // Triggered whenever the engine observes zero open positions (including on
  // initial page load with no orders yet). The "fresh trade" defaults are
  // distinct from DEFAULT_TRAILING_STOP_PARAMS. `mode` is preserved.
  // Idempotent: no-op (and no signal bump) if params are already at defaults,
  // so the 5-minute poll doesn't spam the log or re-trigger persistence.
  private resetParamsToDefaults(): void {
    const alreadyReset =
      this.params.profitReach === RESET_TRAILING_STOP_PARAMS.profitReach &&
      this.params.step === RESET_TRAILING_STOP_PARAMS.step &&
      this.params.takeProfit === RESET_TRAILING_STOP_PARAMS.takeProfit &&
      this.params.stopLoss === RESET_TRAILING_STOP_PARAMS.stopLoss &&
      this.params.usePriceSLTP === RESET_TRAILING_STOP_PARAMS.usePriceSLTP &&
      this.params.takeProfitPrice === RESET_TRAILING_STOP_PARAMS.takeProfitPrice &&
      this.params.stopLossPrice === RESET_TRAILING_STOP_PARAMS.stopLossPrice;

    if (alreadyReset) return;

    this.params = {
      ...RESET_TRAILING_STOP_PARAMS,
      mode: this.params.mode,
    };
    this.paramsResetSignal += 1;
    this.alerts.push({
      id: genAlertId(),
      type: 'info',
      message: 'No open positions — trailing stop settings reset to defaults',
      timestamp: Date.now(),
    });
  }

  // ─── Re-apply updated settings to open positions ─────────────
  // Called when the user changes trailing settings while positions are running.
  // - TP: always re-applied (TP is a fixed price level based on takeProfit).
  // - SL: only re-applied if the engine has NOT yet trailed it (still at initial SL),
  //   so we never pull a trailing SL backwards.
  private async reapplyParamsToPositions(): Promise<void> {
    for (const pos of Object.values(this.positions)) {
      if (!pos.isForex || !pos.sltpSetupDone) continue;
      if (this.amendPending.has(pos.positionId)) continue;
      if (this.tpClosePending.has(pos.positionId)) continue;

      const pip = pipSizeDecimal(pos.digits);
      const tol = pip * 0.5;

      // Compute desired TP/SL — either from absolute price values (usePriceSLTP)
      // or from pip offsets relative to entry.
      let newTP: number | null = null;
      let newInitialSL: number | null = null;

      if (this.params.usePriceSLTP) {
        if (this.params.takeProfitPrice !== null) {
          newTP = roundPrice(this.params.takeProfitPrice, pos.digits);
        }
        if (this.params.stopLossPrice !== null) {
          newInitialSL = roundPrice(this.params.stopLossPrice, pos.digits);
        }
      } else {
        newTP = roundPrice(
          pos.direction === 'buy'
            ? pos.entryPrice + this.params.takeProfit * pip
            : pos.entryPrice - this.params.takeProfit * pip,
          pos.digits,
        );
        newInitialSL = roundPrice(
          pos.direction === 'buy'
            ? pos.entryPrice - this.params.stopLoss * pip
            : pos.entryPrice + this.params.stopLoss * pip,
          pos.digits,
        );
      }

      let slTarget: number | null = null;
      let tpTarget: number | null = null;

      // TP: always re-apply if it differs from what's currently on cTrader
      if (newTP !== null && (pos.currentTP === null || Math.abs(newTP - pos.currentTP) >= tol)) {
        tpTarget = newTP;
      }

      // SL: only re-apply while still at the initial SL (not yet trailed)
      if (!pos.slTrailed) {
        if (newInitialSL !== null && (pos.currentSL === null || Math.abs(newInitialSL - pos.currentSL) >= tol)) {
          slTarget = newInitialSL;
        }
      }

      if (slTarget === null && tpTarget === null) continue;

      pos.targetSL = slTarget !== null ? slTarget : pos.targetSL;
      pos.targetTP = tpTarget !== null ? tpTarget : pos.targetTP;
      pos.lastSLModifyTime = Date.now();

      // Send the FULL desired state: a level that is NOT being changed is sent
      // with its CURRENT value so cTrader keeps it (a null would clear it).
      const slSend = slTarget !== null ? slTarget : pos.currentSL;
      const tpSend = tpTarget !== null ? tpTarget : pos.currentTP;
      this.sendAmendSLTP(pos.positionId, pos.digits, slSend, tpSend)
        .then((success) => {
          if (success) {
            if (slTarget !== null) pos.currentSL = slTarget;
            if (tpTarget !== null) pos.currentTP = tpTarget;
            this.alerts.push({
              id: genAlertId(),
              type: 'info',
              message: `Settings applied: ${pos.symbolName} | ${
                slTarget !== null ? `SL→${slTarget.toFixed(pos.digits)}` : ''
              }${slTarget !== null && tpTarget !== null ? ' ' : ''}${
                tpTarget !== null ? `TP→${tpTarget.toFixed(pos.digits)}` : ''
              }`,
              timestamp: Date.now(),
              symbolName: pos.symbolName,
            });
            this.notify();
          } else {
            if (slTarget !== null) pos.targetSL = pos.currentSL;
            if (tpTarget !== null) pos.targetTP = pos.currentTP;
          }
        });
    }
  }

  subscribe(fn: (s: TrailingStopSnapshot) => void): () => void {
    this.listeners.add(fn);
    fn(this.snapshot());
    return () => { this.listeners.delete(fn); };
  }

  private snapshot(): TrailingStopSnapshot {
    return {
      positions: { ...this.positions },
      lastTick: this.lastTick,
      isRunning: this.running,
      selectedAccountId: this.accountId,
      alerts: [...this.alerts],
      streamActive: this.streamActive,
      loading: this.loading,
      lastError: this.lastError,
      currentMode: this.params.mode,
      depositCurrency: this.depositCurrency,
      paramsResetSignal: this.paramsResetSignal,
    };
  }

  private notify(): void {
    const s = this.snapshot();
    this.listeners.forEach((fn) => fn(s));
  }

  // ─── Initialise ──────────────────────────────────────────────

  async init(accountId: string): Promise<void> {
    // ─── Different account while engine is running? Stop old stream first ───
    if (this.running && this.accountId !== accountId) {
      console.log(`[TrailingEngine] Switching account: ${this.accountId} → ${accountId} — stopping old stream`);
      this.stop();
    }

    if (this.accountId === accountId && this.running) {
      await this.refreshPositions();
      return;
    }

    this.accountId = accountId;
    this.loading = true;
    this.lastError = '';
    this.notify();

    try {
      await this.refreshPositions();
    } catch (err) {
      this.lastError = `Failed to initialize: ${(err as Error).message}`;
    }

    this.loading = false;
    this.notify();
  }

  // ─── Refresh positions from cTrader ──────────────────────────
  // Reads the shared stream's snapshot (traderRes + reconcileRes + symbolRes)
  // and parses it the same way as AccountDataHub (dashboard).

  async refreshPositions(): Promise<void> {
    if (!this.accountId) {
      console.warn('[TrailingEngine] refreshPositions skipped: no accountId');
      return;
    }

    const numId = parseInt(this.accountId, 10);
    if (isNaN(numId)) {
      console.warn('[TrailingEngine] refreshPositions skipped: invalid accountId', this.accountId);
      return;
    }

    try {
      console.log(`[TrailingEngine] Reading stream snapshot for accountId=${numId}...`);

      const data = await getAccountData(numId);

      console.log('[TrailingEngine] invoke result:', {
        hasData: !!data,
        success: data?.success,
        hasReconcile: !!data?.reconcileRes,
        hasTrader: !!data?.traderRes,
        hasSymbol: !!data?.symbolRes,
        error: data?.error,
        log: data?.log,
      });

      if (!data) {
        // Stream snapshot not ready yet — the shared stream is still
        // connecting / performing its first reconcile. Keep current state;
        // the positions-bus subscription refreshes once data arrives.
        return;
      }

      if (!data.success) {
        this.lastError = `cTrader returned error: ${data.error || 'unknown'}`;
        this.notify();
        return;
      }

      if (!data.reconcileRes) {
        // Trader snapshot present but positions not reconciled yet (transient).
        this.lastError = '';
        this.positions = {};
        this.notify();
        return;
      }

      // ─── Parse traderRes → moneyDigits (nested in trader object!) ───
      let moneyDigits = 2;
      let leverage: string | null = null;
      if (data.traderRes) {
        const parsedTrader = parseTraderRes(data.traderRes, numId);
        // moneyDigits lives in traderRes.trader.moneyDigits, not traderRes.moneyDigits
        const traderObj = (data.traderRes.trader as Record<string, unknown>) || {};
        if (typeof traderObj.moneyDigits === 'number') {
          moneyDigits = traderObj.moneyDigits as number;
        }
        leverage = parsedTrader.leverage ?? null;
        this.depositCurrency = parsedTrader.depositAsset || '';
        console.log(`[TrailingEngine] traderRes: moneyDigits=${moneyDigits} leverage=${leverage} deposit=${this.depositCurrency}`);
      }

      // ─── Parse symbolRes ───
      const symbolMap = data.symbolRes ? parseSymbolRes(data.symbolRes) : {};
      const symKeys = Object.keys(symbolMap);
      console.log(`[TrailingEngine] symbolMap: ${symKeys.length} entries, sample:`, symKeys.slice(0, 5).map(k => `${k}=${symbolMap[Number(k)]}`));

      // ─── Parse reconcileRes ───
      const parsed = parseReconcileRes(data.reconcileRes, moneyDigits, symbolMap, leverage);
      console.log(`[TrailingEngine] parseReconcileRes: ${parsed.positions.length} positions`);
      if (parsed.positions.length > 0) {
        parsed.positions.forEach((p) => {
          console.log(`  pos #${p.positionId}: sym=${p.symbolName || '?'} side=${p.tradeSide} entry=${p.entryPrice} sl=${p.sl} tp=${p.tp} vol=${p.rawVolume} pnl=${p.unrealizedPnl}`);
        });
      }

      if (parsed.positions.length === 0) {
        this.lastError = 'No open positions on this account.';
        this.positions = {};
        this.resetParamsToDefaults();
        this.notify();
        return;
      }

      const newPositions: Record<string, TrackedPosition> = {};
      let symbolIdsChanged = false;

      for (const p of parsed.positions) {
        const pid = String(p.positionId);
        const symName = p.symbolName || `Symbol#${p.symbolId || 0}`;
        const symId = p.symbolId || 0;
        const digits = guessDigits(symName);
        const scale = priceScale(digits);
        const dir: 'buy' | 'sell' = p.tradeSide === 'BUY' ? 'buy' : 'sell';
        const forex = isForexSymbol(symName);

        // cTrader prices from parseReconcileRes are ALREADY decimal (protobuf decoder scaled them).
        // DO NOT call toDecimal() again — that would double-scale and produce values like 0.00001.
        const entryDec = p.entryPrice;           // decimal (e.g. 0.81700)
        const curDec = entryDec;                 // initial: same as entry, updated by spot ticks
        const slDec = p.sl !== null ? p.sl : null;
        const tpDec = p.tp !== null ? p.tp : null;
        const profit = calcProfitPips(entryDec, curDec, dir, digits);
        console.log(
          `[TrailingEngine] pos#${pid}: ${symName} ${dir} entry=${entryDec.toFixed(digits)} sl=${slDec?.toFixed(digits)} tp=${tpDec?.toFixed(digits)} forex=${forex}`,
        );

        const existing = this.positions[pid];

        if (!existing) {
          this.alerts.push({
            id: genAlertId(),
            type: 'position_opened',
            message: `${dir.toUpperCase()} ${symName} @ ${entryDec.toFixed(digits)}${forex ? '' : ' [non-Forex]'}`,
            timestamp: Date.now(),
            symbolName: symName,
          });
          symbolIdsChanged = true;
        }

        // Compute initial SL/TP targets for forex positions
        let targetSL: number | null = null;
        let targetTP: number | null = null;
        let sltpDone = existing?.sltpSetupDone || false;

        if (forex) {
          const pip = pipSizeDecimal(digits);

          // Compute desired initial SL/TP — from absolute price values
          // (usePriceSLTP) or from pip offsets relative to entry.
          if (this.params.usePriceSLTP) {
            targetSL = this.params.stopLossPrice !== null
              ? roundPrice(this.params.stopLossPrice, digits)
              : null;
            targetTP = this.params.takeProfitPrice !== null
              ? roundPrice(this.params.takeProfitPrice, digits)
              : null;
          } else {
            targetSL = roundPrice(
              dir === 'buy'
                ? entryDec - this.params.stopLoss * pip
                : entryDec + this.params.stopLoss * pip,
              digits,
            );
            targetTP = roundPrice(
              dir === 'buy'
                ? entryDec + this.params.takeProfit * pip
                : entryDec - this.params.takeProfit * pip,
              digits,
            );
          }

          // STEP 1: Set SL/TP if not already present on cTrader
          const needsSL = slDec === null || slDec === 0;
          const needsTP = tpDec === null || tpDec === 0;

          // Only attempt to set a level we actually have a target for.
          const doSL = needsSL && targetSL !== null;
          const doTP = needsTP && targetTP !== null;

          if ((doSL || doTP) && !this.amendPending.has(pid) && !sltpDone) {
            sltpDone = false;
            // Pass the CURRENT levels for anything we are NOT setting, so an
            // amend for one level never clears the other.
            this.sendAmendSLTP(pid, digits, doSL ? targetSL : slDec, doTP ? targetTP : tpDec)
              .then((success) => {
                if (success) {
                  const pos = this.positions[pid];
                  if (pos) {
                    pos.sltpSetupDone = true;
                    if (doSL && targetSL !== null) {
                      pos.currentSL = targetSL;
                      pos.targetSL = targetSL;
                    }
                    if (doTP && targetTP !== null) {
                      pos.currentTP = targetTP;
                      pos.targetTP = targetTP;
                    }
                    const slLabel = doSL && targetSL !== null ? `SL=${targetSL.toFixed(digits)}` : '';
                    const tpLabel = doTP && targetTP !== null ? `TP=${targetTP.toFixed(digits)}` : '';
                    const parts = [slLabel, tpLabel].filter(Boolean).join(' ');
                    this.alerts.push({
                      id: genAlertId(),
                      type: 'initial_sltp_set',
                      message: `SL/TP set: ${symName}${parts ? ` | ${parts}` : ''}`,
                      timestamp: Date.now(),
                      symbolName: symName,
                    });
                    this.notify();
                  }
                }
              });
          } else if (!needsSL && !needsTP && slDec !== null && tpDec !== null) {
            sltpDone = true;
            targetSL = slDec;
            targetTP = tpDec;
          }
        }

        // Entry time
        const rawPos = p.raw as Record<string, unknown>;
        const td = (rawPos.tradeData as Record<string, unknown>) || {};
        let entryTime = existing?.entryTime || Date.now();
        if (typeof td.openTimestamp === 'number') entryTime = td.openTimestamp;
        else if (typeof rawPos.openTimestamp === 'number') entryTime = rawPos.openTimestamp as number;
        else if (typeof rawPos.timestamp === 'number') entryTime = rawPos.timestamp as number;

        newPositions[pid] = {
          positionId: pid,
          symbolName: symName,
          symbolId: symId,
          digits,
          direction: dir,
          entryPrice: entryDec,
          currentPrice: curDec,
          currentSL: slDec,
          currentTP: tpDec,
          targetSL,
          targetTP,
          sltpSetupDone: sltpDone,
          slTrailed: existing?.slTrailed || false,
          isForex: forex,
          rawVolume: p.rawVolume,
          volume: p.volume,
          profitPips: profit,
          entryTime,
          lastSLModifyTime: existing?.lastSLModifyTime || 0,
          priceScale: scale,
        };
      }

      // Detect closed positions
      for (const oldPid of Object.keys(this.positions)) {
        if (!newPositions[oldPid]) {
          const closed = this.positions[oldPid];
          this.alerts.push({
            id: genAlertId(),
            type: 'position_closed',
            message: `Closed: ${closed.direction.toUpperCase()} ${closed.symbolName} (${closed.profitPips.toFixed(1)} pips)`,
            timestamp: Date.now(),
            symbolName: closed.symbolName,
          });
          this.amendPending.delete(oldPid);
        }
      }

      this.positions = newPositions;
      this.lastTick = Date.now();
      this.lastError = '';

      if (this.alerts.length > 100) this.alerts = this.alerts.slice(-100);

      console.log(`[TrailingEngine] refreshPositions done: ${Object.keys(newPositions).length} positions, notify + stream`);
      this.notify(); // ← CRITICAL: must notify UI!

      if (symbolIdsChanged && this.running) {
        this.ensurePriceStream();
      }
    } catch (err) {
      console.error('[TrailingEngine] refreshPositions exception:', err);
      this.lastError = `Network error: ${(err as Error).message}`;
      this.notify(); // ← Show error in UI
    }
  }

  // ─── Send amend SL/TP to cTrader ─────────────────────────────

  private async sendAmendSLTP(
    positionId: string,
    digits: number,
    slDecimal: number | null,
    tpDecimal: number | null,
  ): Promise<boolean> {
    if (!this.accountId) return false;
    const numAccountId = parseInt(this.accountId, 10);
    if (isNaN(numAccountId)) return false;

    const posIdNum = parseInt(positionId, 10);
    if (isNaN(posIdNum)) return false;

    this.amendPending.add(positionId);

    try {
      // cTrader's AmendPositionSltp RESETS any level that is omitted from the
      // request (a missing stopLoss/takeProfit is treated as 0 = "remove").
      // That is exactly why changing only ONE level used to wipe the other
      // (editing SL erased the TP, and vice-versa). We therefore ALWAYS send
      // BOTH levels:
      //   • slDecimal / tpDecimal = null  → 0  (meaning "no SL/TP")
      //   • callers pass the CURRENT value for a level that must stay as-is.
      // Prices are DECIMAL (same as display), rounded to exact digits.
      const payload: Record<string, unknown> = {
        action: 'amend-sltp',
        accountId: numAccountId,
        positionId: posIdNum,
        stopLoss: slDecimal !== null ? roundPrice(slDecimal, digits) : 0,
        takeProfit: tpDecimal !== null ? roundPrice(tpDecimal, digits) : 0,
      };

      console.log(`[TrailingEngine] amend-sltp pid=${positionId} dig=${digits} sl=${payload.stopLoss} tp=${payload.takeProfit}`);

      const { data } = await supabase.functions.invoke<{
        success: boolean;
        error?: string;
      }>('ctrader-gateway', { method: 'POST', body: payload });

      if (!data?.success) {
        console.warn(`[TrailingEngine] amend-sltp failed for ${positionId}: ${data?.error || 'unknown'}`);
        this.alerts.push({
          id: genAlertId(),
          type: 'error',
          message: `Failed to modify SL/TP for position ${positionId}: ${data?.error || 'unknown'}`,
          timestamp: Date.now(),
        });
        this.notify();
        return false;
      }

      return true;
    } catch (err) {
      console.warn(`[TrailingEngine] amend-sltp error for ${positionId}:`, err);
      return false;
    } finally {
      this.amendPending.delete(positionId);
    }
  }

  // ─── Close a position on cTrader (manual only) ──────────────
  // The engine does NOT auto-close positions anymore — cTrader itself closes
  // at the TP set in STEP 1. sendClosePosition backs ONLY explicit user
  // actions (Close one / Close all from the monitor UI).

  private async sendClosePosition(positionId: string, volume: number): Promise<boolean> {
    if (!this.accountId) return false;
    const numAccountId = parseInt(this.accountId, 10);
    if (isNaN(numAccountId)) return false;

    const posIdNum = parseInt(positionId, 10);
    if (isNaN(posIdNum)) return false;

    try {
      const payload: Record<string, unknown> = {
        action: 'close-position',
        accountId: numAccountId,
        positionId: posIdNum,
      };
      if (volume > 0) payload.volume = volume;

      console.log(`[TrailingEngine] close-position pid=${positionId} vol=${volume}`);

      const { data } = await supabase.functions.invoke<{
        success: boolean;
        error?: string;
      }>('ctrader-gateway', { method: 'POST', body: payload });

      if (!data?.success) {
        console.warn(`[TrailingEngine] close-position failed for ${positionId}: ${data?.error || 'unknown'}`);
        this.alerts.push({
          id: genAlertId(),
          type: 'error',
          message: `Failed to close position ${positionId} (TP hit): ${data?.error || 'unknown'}`,
          timestamp: Date.now(),
        });
        this.notify();
        return false;
      }

      return true;
    } catch (err) {
      console.warn(`[TrailingEngine] close-position error for ${positionId}:`, err);
      return false;
    }
  }

  // ─── Public close position from UI ─────────────────────────

  async closePositionById(positionId: string): Promise<boolean> {
    const pos = this.positions[positionId];
    if (!pos) {
      console.warn(`[TrailingEngine] closePositionById: position ${positionId} not found`);
      return false;
    }
    if (this.tpClosePending.has(positionId) || this.amendPending.has(positionId)) {
      console.warn(`[TrailingEngine] closePositionById: position ${positionId} has pending operation`);
      return false;
    }
    this.tpClosePending.add(positionId);
    try {
      const success = await this.sendClosePosition(positionId, pos.rawVolume);
      if (success) {
        this.alerts.push({
          id: genAlertId(),
          type: 'position_closed',
          message: `Manually closed: ${pos.direction.toUpperCase()} ${pos.symbolName} @ ${pos.currentPrice.toFixed(pos.digits)} | Profit=${pos.profitPips.toFixed(1)} pips`,
          timestamp: Date.now(),
          symbolName: pos.symbolName,
        });
        delete this.positions[positionId];
        this.amendPending.delete(positionId);
        this.notify();
      }
      return success;
    } catch (err) {
      console.warn(`[TrailingEngine] closePositionById error for ${positionId}:`, err);
      return false;
    } finally {
      this.tpClosePending.delete(positionId);
    }
  }

  async closeAllPositions(): Promise<{ success: number; failed: number }> {
    const ids = Object.keys(this.positions);
    if (ids.length === 0) return { success: 0, failed: 0 };

    let success = 0;
    let failed = 0;

    // Close all in parallel for speed
    const results = await Promise.all(ids.map((id) => this.closePositionById(id)));
    results.forEach((r) => {
      if (r) success++;
      else failed++;
    });

    this.alerts.push({
      id: genAlertId(),
      type: 'position_closed',
      message: `Batch close: ${success} closed, ${failed} failed out of ${ids.length} positions`,
      timestamp: Date.now(),
    });
    this.notify();

    return { success, failed };
  }

  // ─── Ensure price stream for this account ────────────────────

  private ensurePriceStream(): void {
    if (!this.accountId) return;
    const numId = parseInt(this.accountId, 10);
    if (isNaN(numId)) return;

    const symbolIds = [...new Set(Object.values(this.positions).map((p) => p.symbolId))];
    if (symbolIds.length === 0) return;

    const digitsMap: Record<number, number> = {};
    for (const p of Object.values(this.positions)) {
      digitsMap[p.symbolId] = p.digits;
    }

    updateStreamSymbols(numId, symbolIds, digitsMap);
    this.streamActive = true;
  }

  // ─── Process a single spot tick ──────────────────────────────

  private onSpotTick = (tick: SpotTick): void => {
    if (!this.running || this.loading || tick.symbolId === null) return;

    const symbolId = tick.symbolId;

    // Find all positions for this symbol
    const matchingPositions = Object.values(this.positions).filter(
      (p) => p.symbolId === symbolId,
    );

    if (matchingPositions.length === 0) return;

    this.streamActive = true;
    let changed = false;

    for (const pos of matchingPositions) {
      // Pick correct price based on direction
      // Spot tick prices are already decimal (edge function divides by 100000)
      let curPrice: number | null = null;
      if (pos.direction === 'buy') {
        curPrice = tick.bid;
      } else {
        curPrice = tick.ask;
      }

      if (curPrice === null || curPrice <= 0) continue;

      pos.currentPrice = curPrice;
      pos.profitPips = calcProfitPips(pos.entryPrice, curPrice, pos.direction, pos.digits);
      changed = true;

      // ─── STEP 2: Trailing (only if forex, and SL/TP setup done) ───
      if (pos.isForex && pos.sltpSetupDone && this.params.mode !== 'setupOnly') {
        this.evaluateTrailing(pos);
      }

      // ─── TP reached: INFORMATIONAL ONLY (no auto-close) ───
      // The engine NO LONGER force-closes positions. The TP set on cTrader
      // (STEP 1) is the single source of truth and the broker closes the
      // position itself. Removing the engine-side close prevents surprise
      // closes — especially for non-forex symbols that never had a TP sent to
      // cTrader, and in 'setupOnly' mode where nothing should be managed.
      let tpHit = false;
      let tpDesc = '';
      if (this.params.usePriceSLTP) {
        if (this.params.takeProfitPrice !== null) {
          tpHit = pos.direction === 'buy'
            ? pos.currentPrice >= this.params.takeProfitPrice
            : pos.currentPrice <= this.params.takeProfitPrice;
          tpDesc = this.params.takeProfitPrice.toFixed(pos.digits);
        }
      } else {
        tpHit = pos.profitPips >= this.params.takeProfit;
        tpDesc = `${this.params.takeProfit} pips`;
      }

      if (tpHit) {
        const lastTpAlert = this.alerts.findLast(
          (a) => a.type === 'tp_hit' && a.symbolName === pos.symbolName,
        );
        if (!lastTpAlert || Date.now() - lastTpAlert.timestamp > 30000) {
          this.alerts.push({
            id: genAlertId(),
            type: 'tp_hit',
            message: `TP reached: ${pos.symbolName} (TP=${tpDesc}) — broker will close`,
            timestamp: Date.now(),
            symbolName: pos.symbolName,
          });
        }
      }
    }

    this.resetFallbackTimer();

    if (this.alerts.length > 50) this.alerts = this.alerts.slice(-50);

    if (changed) {
      this.lastTick = Date.now();
      this.notify();
    }
  };

  // ─── Evaluate trailing for a position ────────────────────────
  // Translated directly from C# Trail() algorithm:
  //
  // Full mode:
  //   1. profitPips >= profitReach → breakeven: SL = entry ± 1 pip
  //   2. For i=1..200: profitPips >= profitReach + i*step → SL = entry ± i*step*pip
  // Simple mode:
  //   1. profitPips >= profitReach → breakeven: SL = entry ± 1 pip
  //   No stepping.
  //
  // Both modes use CURRENT profitPips (not highest), with SL position
  // acting as ratchet: SL only moves forward, never backward.

  private evaluateTrailing(pos: TrackedPosition): void {
    if (this.amendPending.has(pos.positionId)) return;
    if (pos.currentSL === null) return;
    if (pos.profitPips < this.params.profitReach) return;

    // Rate limit: don't modify same position too frequently
    if (Date.now() - pos.lastSLModifyTime < MIN_AMEND_INTERVAL_MS) return;

    const pip = pipSizeDecimal(pos.digits);
    let desiredSL: number | null = null;

    // ─── Breakeven (+1 pip) ───
    // C#: if (profitPips >= profitReach)
    //       Buy  & SL < entryPrice  → SL = entryPrice + 1 * pipSize
    //       Sell & SL > entryPrice  → SL = entryPrice - 1 * pipSize
    if (pos.direction === 'buy' && pos.currentSL < pos.entryPrice) {
      desiredSL = roundPrice(pos.entryPrice + pip, pos.digits);
    } else if (pos.direction === 'sell' && pos.currentSL > pos.entryPrice) {
      desiredSL = roundPrice(pos.entryPrice - pip, pos.digits);
    }

    // ─── Stepping loop (Full mode only) ───
    // C#: for (int i = 1; i <= 200; i++)
    //        if (profitPips >= profitReach + i * step)
    //          Buy  & SL < entry + i*step*pip  → SL = entry + i*step*pip
    //          Sell & SL > entry - i*step*pip  → SL = entry - i*step*pip
    if (this.params.mode === 'full') {
      for (let i = 1; i <= MAX_TRAILING_STEPS; i++) {
        if (pos.profitPips >= this.params.profitReach + i * this.params.step) {
          const stepTarget = pos.direction === 'buy'
            ? roundPrice(pos.entryPrice + i * this.params.step * pip, pos.digits)
            : roundPrice(pos.entryPrice - i * this.params.step * pip, pos.digits);

          // Ratchet: only move if current SL hasn't reached this step yet
          if (
            (pos.direction === 'buy' && pos.currentSL < stepTarget) ||
            (pos.direction === 'sell' && pos.currentSL > stepTarget)
          ) {
            desiredSL = stepTarget;
          }
        }
      }
    }

    if (desiredSL === null) return;

    // Check if this is actually a change (tolerance = half pip)
    const tolerance = pip * 0.5;
    if (Math.abs(desiredSL - pos.currentSL) < tolerance) return;

    // ─── Send amend ───
    const prevSL = pos.currentSL;
    pos.targetSL = desiredSL;
    pos.lastSLModifyTime = Date.now();

    // Only the SL is being trailed — pass the CURRENT TP so cTrader keeps it.
    this.sendAmendSLTP(pos.positionId, pos.digits, desiredSL, pos.currentTP)
      .then((success) => {
        if (success) {
          pos.currentSL = desiredSL;
          pos.slTrailed = true;
          const isBreakeven = pos.direction === 'buy'
            ? desiredSL <= pos.entryPrice + pip + tolerance
            : desiredSL >= pos.entryPrice - pip - tolerance;
          this.alerts.push({
            id: genAlertId(),
            type: isBreakeven ? 'breakeven' : 'sl_trailed',
            message: isBreakeven
              ? `Breakeven: ${pos.symbolName} ${pos.direction.toUpperCase()} | SL → entry±1pip (${pos.entryPrice.toFixed(pos.digits)}) | Profit=${pos.profitPips.toFixed(1)} pips`
              : `SL trailed: ${pos.symbolName} ${pos.direction.toUpperCase()} | SL ${prevSL?.toFixed(pos.digits) || 'N/A'} → ${desiredSL.toFixed(pos.digits)} | Profit=${pos.profitPips.toFixed(1)} pips`,
            timestamp: Date.now(),
            symbolName: pos.symbolName,
          });
          this.notify();
        } else {
          // Rollback on failure
          pos.targetSL = pos.currentSL;
        }
      });
  }

  // ─── Fallback: HTTP price fetch if stream stalls ─────────────

  private resetFallbackTimer(): void {
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
    this.fallbackTimer = setTimeout(() => {
      if (this.running) {
        this.streamActive = false;
        this.fallbackFetchPrices();
      }
    }, 10000);
  }

  private async fallbackFetchPrices(): Promise<void> {
    if (!this.running || !this.accountId) return;

    const symbolIds = [...new Set(Object.values(this.positions).map((p) => p.symbolId))];
    if (symbolIds.length === 0) return;

    try {
      const numId = parseInt(this.accountId, 10);
      const { data: spotData } = await supabase.functions.invoke<{
        success: boolean;
        spotPrices: Record<number, { bid: number | null; ask: number | null }>;
      }>('ctrader-gateway', {
        method: 'POST',
        body: { action: 'get-spot-prices', accountId: numId, symbolIds },
      });

      if (!spotData?.success || !spotData.spotPrices) return;

      for (const [sid, spot] of Object.entries(spotData.spotPrices)) {
        this.onSpotTick({
          symbolId: parseInt(sid, 10),
          bid: spot.bid,
          ask: spot.ask,
          timestamp: Date.now(),
        });
      }
    } catch (err) {
      console.warn('[TrailingEngine] Fallback fetch failed:', err);
    }
  }

  // ─── Start / Stop ────────────────────────────────────────────

  start(): void {
    if (this.running) return;
    this.running = true;
    this.streamActive = false;

    this.unsubBus = ctraderPriceBus.subscribe(this.onSpotTick);

    // Refresh positions as soon as the shared stream reconciles them, so we
    // don't wait for the 5-min poll. Throttled to avoid redundant re-parses.
    this.unsubPositions = copyTradePositionsBus.subscribe((snapshot) => {
      const numId = parseInt(this.accountId, 10);
      if (!this.running || isNaN(numId) || snapshot.accountId !== numId) return;
      if (Date.now() - this.lastPositionsRefresh < 2000) return;
      this.lastPositionsRefresh = Date.now();
      void this.refreshPositions();
    });

    // Bootstrap the shared stream (empty symbol list) so its server-driven
    // reconcile pulls open positions + trader data on the single cTrader
    // connection. This replaces the old get-account-data gateway fallback.
    const bootstrapId = parseInt(this.accountId, 10);
    if (!isNaN(bootstrapId)) {
      ensureStream(bootstrapId, [], {});
    }

    this.refreshInterval = setInterval(() => {
      if (this.running) this.refreshPositions();
    }, POSITION_REFRESH_INTERVAL_MS);

    this.refreshPositions().then(() => {
      if (this.running) this.ensurePriceStream();
      this.resetFallbackTimer();
    });

    this.notify();
    console.log(`[TrailingEngine] Started for account ${this.accountId} | mode=${this.params.mode}`);
  }

  stop(): void {
    this.running = false;

    if (this.unsubBus) {
      this.unsubBus();
      this.unsubBus = null;
    }

    if (this.unsubPositions) {
      this.unsubPositions();
      this.unsubPositions = null;
    }

    const numId = parseInt(this.accountId, 10);
    if (!isNaN(numId)) {
      releaseStream(numId);
    }

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
    if (this.fallbackTimer) {
      clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }

    this.streamActive = false;
    this.notify();
    console.log(`[TrailingEngine] Stopped for account ${this.accountId}`);
  }

  destroy(): void {
    this.stop();
    this.listeners.clear();
  }

  resetAlerts(): void {
    this.alerts = [];
    this.notify();
  }
}

// ─── Per-Account Registry ────────────────────────────────────
// Each (strategyId, accountId) pair gets its own independent engine,
// so two accounts running the same strategy never overlap streams/state.

const engines = new Map<string, TrailingStopEngine>();

function engineKey(strategyId: string, accountId: string): string {
  return buildSessionKey(strategyId, accountId);
}

export function getOrCreateTrailingEngine(
  strategyId: string,
  accountId: string,
  params: TrailingStopParams,
): TrailingStopEngine {
  const key = engineKey(strategyId, accountId);
  let eng = engines.get(key);
  if (!eng) {
    eng = new TrailingStopEngine(params);
    engines.set(key, eng);
  } else {
    eng.setParams(params);
  }
  return eng;
}

export function getTrailingEngine(strategyId: string, accountId: string): TrailingStopEngine | undefined {
  return engines.get(engineKey(strategyId, accountId));
}

export function getAllTrailingEngines(): TrailingStopEngine[] {
  return [...engines.values()];
}

export function destroyTrailingEngine(strategyId: string, accountId: string): void {
  const key = engineKey(strategyId, accountId);
  const eng = engines.get(key);
  if (eng) {
    eng.destroy();
    engines.delete(key);
  }
}